import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { animationConfig, createAnimation, createStaggeredAnimation } from '../../lib/animations';

// Animated Container with stagger effect
export const AnimatedContainer = ({ children, className = '', delay = 0, staggerDelay = 0.1, ...props }) => {
  const stagger = createStaggeredAnimation(delay, staggerDelay);
  
  return (
    <motion.div
      className={className}
      variants={stagger.container}
      initial="initial"
      animate="animate"
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Animated Item for stagger animations
export const AnimatedItem = ({ children, className = '', ...props }) => {
  const stagger = createStaggeredAnimation();
  
  return (
    <motion.div
      className={className}
      variants={stagger.item}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Fade In component
export const FadeIn = ({ children, className = '', duration = 'normal', easing = 'material', delay = 0, ...props }) => {
  const animation = createAnimation('fadeIn', duration, easing);
  
  return (
    <motion.div
      className={className}
      variants={animation}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ ...animation.transition, delay }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Fade In Up component
export const FadeInUp = ({ children, className = '', duration = 'normal', easing = 'material', delay = 0, ...props }) => {
  const animation = createAnimation('fadeInUp', duration, easing);
  
  return (
    <motion.div
      className={className}
      variants={animation}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ ...animation.transition, delay }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Fade In Down component
export const FadeInDown = ({ children, className = '', duration = 'normal', easing = 'material', delay = 0, ...props }) => {
  const animation = createAnimation('fadeInDown', duration, easing);
  
  return (
    <motion.div
      className={className}
      variants={animation}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ ...animation.transition, delay }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Scale In component
export const ScaleIn = ({ children, className = '', duration = 'normal', easing = 'material', delay = 0, ...props }) => {
  const animation = createAnimation('scaleIn', duration, easing);
  
  return (
    <motion.div
      className={className}
      variants={animation}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ ...animation.transition, delay }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Slide Up component
export const SlideUp = ({ children, className = '', duration = 'normal', easing = 'material', delay = 0, ...props }) => {
  const animation = createAnimation('slideUp', duration, easing);
  
  return (
    <motion.div
      className={className}
      variants={animation}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ ...animation.transition, delay }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Slide Down component
export const SlideDown = ({ children, className = '', duration = 'normal', easing = 'material', delay = 0, ...props }) => {
  const animation = createAnimation('slideDown', duration, easing);
  
  return (
    <motion.div
      className={className}
      variants={animation}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ ...animation.transition, delay }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Hover Lift component
export const HoverLift = ({ children, className = '', ...props }) => {
  return (
    <motion.div
      className={className}
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={animationConfig.transitions.default}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Hover Scale component
export const HoverScale = ({ children, className = '', scale = 1.05, ...props }) => {
  return (
    <motion.div
      className={className}
      whileHover={{ scale }}
      whileTap={{ scale: 0.95 }}
      transition={animationConfig.transitions.fast}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Page Transition component
export const PageTransition = ({ children, className = '', ...props }) => {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={animationConfig.transitions.default}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Modal Backdrop component
export const ModalBackdrop = ({ children, className = '', isOpen, onClose, ...props }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 ${className}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={animationConfig.transitions.fast}
          onClick={onClose}
          {...props}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Modal Content component
export const ModalContent = ({ children, className = '', ...props }) => {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      transition={animationConfig.transitions.default}
      onClick={(e) => e.stopPropagation()}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// List Item component with stagger
export const AnimatedListItem = ({ children, className = '', index = 0, ...props }) => {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: animationConfig.duration.normal,
        ease: animationConfig.easing.material,
        delay: index * 0.1,
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Loading Spinner component
export const LoadingSpinner = ({ className = '', size = 'md', ...props }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
  };

  return (
    <motion.div
      className={`${sizeClasses[size]} border-2 border-gray-300 border-t-blue-600 rounded-full ${className}`}
      animate={{ rotate: 360 }}
      transition={{
        duration: 1,
        ease: 'linear',
        repeat: Infinity,
      }}
      {...props}
    />
  );
};

// Pulse component
export const Pulse = ({ children, className = '', ...props }) => {
  return (
    <motion.div
      className={className}
      animate={{ scale: [1, 1.05, 1] }}
      transition={{
        duration: 2,
        ease: 'easeInOut',
        repeat: Infinity,
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Export the animation config for direct use
export { animationConfig, createAnimation, createStaggeredAnimation };
